import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Lottie from 'lottie-react';
import loadingAnimation from '../Animations/loading.json';
import { toast } from 'react-toastify';
import { useProfileStats } from '../../context/ProfileContext';
import { setCookieIssueFlag, detectCookieIssues, storeAuthData } from '../../utils/cookieHelper';
import { API_URL } from '../../config';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Create a flag to prevent state updates after component unmounts
    let isComponentMounted = true;
    
    const processOAuthCallback = async () => {
      try {
        // Get URL parameters from Google OAuth callback
        const params = new URLSearchParams(location.search);
        const error = params.get('error');
        
        if (error) {
          console.error("Authentication error:", error);
          toast.error("Authentication failed: " + error);
          if (isComponentMounted) {
            navigate('/', { replace: true });
          }
          return;
        }
        
        // Check for user parameters - these are sent directly from our backend
        const hasUserParams = Array.from(params.keys()).some(key => 
          ['user_id', 'name', 'email'].includes(key)
        );
       
        if (hasUserParams) {
          // Extract auth_token from URL parameters (fallback for cross-origin cookie issues)
          let authToken = params.get("auth_token");
          
          // Also try to extract from the full URL in case URLSearchParams didn't capture it
          if (!authToken) {
            const fullUrl = window.location.href;
            const urlMatch = fullUrl.match(/[?&]auth_token=([^&]+)/);
            if (urlMatch && urlMatch[1]) {
              try {
                authToken = decodeURIComponent(urlMatch[1]);
                console.log('✅ Auth token extracted from full URL');
              } catch (e) {
                console.warn('Could not decode token from URL:', e);
              }
            }
          }
          
          // Check if token might be truncated (JWT tokens typically have 3 parts separated by dots)
          if (authToken) {
            const tokenParts = authToken.split('.');
            if (tokenParts.length !== 3) {
              console.warn('⚠️ Auth token appears to be truncated or invalid:', {
                tokenLength: authToken.length,
                parts: tokenParts.length,
                tokenPreview: authToken.substring(0, 50) + '...',
                fullUrl: window.location.href.substring(0, 200) + '...'
              });
              // Try to get the full token from the full URL
              const fullUrl = window.location.href;
              // Try multiple regex patterns to find the token
              const patterns = [
                /auth_token=([^&]+)/,
                /auth_token%3D([^&]+)/,
                /auth_token=([^&]*)/g
              ];
              
              let foundToken = null;
              for (const pattern of patterns) {
                const matches = fullUrl.match(pattern);
                if (matches && matches[1]) {
                  try {
                    const decoded = decodeURIComponent(matches[1]);
                    if (decoded.split('.').length === 3) {
                      foundToken = decoded;
                      break;
                    }
                  } catch (e) {
                    // Try without decoding
                    if (matches[1].split('.').length === 3) {
                      foundToken = matches[1];
                      break;
                    }
                  }
                }
              }
              
              if (foundToken) {
                authToken = foundToken;
                console.log('✅ Recovered full auth token from URL');
              } else {
                console.error('❌ Could not recover full auth token. Token may be truncated in URL.');
                authToken = null; // Will rely on cookie or need to fetch token separately
              }
            } else {
              console.log('✅ Auth token extracted successfully from URL parameters', {
                tokenLength: authToken.length,
                preview: authToken.substring(0, 20) + '...' + authToken.substring(authToken.length - 20)
              });
            }
          } else {
            console.warn('⚠️ No auth_token found in URL parameters. Will rely on cookies or need to fetch separately.');
          }
          
          // Construct user data object from URL parameters with better parsing
          const userData = {
            user_id: params.get("user_id"),
            name: params.get("name"),
            email: params.get("email"),
            weight: params.get("weight") ? parseFloat(params.get("weight")) : 0,
            height: params.get("height") ? parseFloat(params.get("height")) : 0,
            age: params.get("age") ? parseInt(params.get("age")) : 0,
            gender: params.get("gender") || "",
            is_authorized: params.get("is_authorized") === "true" || params.get("is_authorized") === true,
            is_google_login: true, // Mark as Google login for reference
            fitness_level: params.get("fitness_level") || "",
            user_type: params.get("user_type") || "free",
            tokens_left: params.get("tokens_left") ? parseInt(params.get("tokens_left")) : 0,
            expiry_date: params.get("expiry_date"),
            subscription_type: params.get("subscription_type") || "Free"
          };
          
          // Add auth_token to userData if present and valid
          if (authToken && authToken.split('.').length === 3) {
            userData.auth_token = authToken;
          }
          
          // Validate essential data
          if (!userData.user_id || !userData.email || !userData.name) {
            console.error('Missing essential user data:', userData);
            toast.error("Authentication failed: Incomplete user data received");
            if (isComponentMounted) {
              navigate('/', { replace: true });
            }
            return;
          }
          
          try {
            // Store user data with timestamp for caching (this will also store auth_token if present)
            storeAuthData(userData);
            
            // Explicitly store auth_token in localStorage for immediate use
            if (authToken && authToken.split('.').length === 3) {
              localStorage.setItem('auth_token', authToken);
              console.log('✅ Auth token stored in localStorage for cross-origin authentication');
            } else {
              // Try to get token from cookies as fallback
              let cookieToken = null;
              try {
                const cookies = document.cookie.split(';');
                for (let cookie of cookies) {
                  const [name, value] = cookie.trim().split('=');
                  if (name === 'access_token' && value) {
                    cookieToken = value;
                    break;
                  }
                }
              } catch (e) {
                console.warn('Could not read cookies:', e);
              }
              
              if (cookieToken && cookieToken.split('.').length === 3) {
                localStorage.setItem('auth_token', cookieToken);
                userData.auth_token = cookieToken;
                console.log('✅ Auth token retrieved from cookies and stored in localStorage');
              } else {
                // Try to fetch token via API as last resort
                console.log('🔄 Attempting to fetch token via API...');
                try {
                  const formData = new FormData();
                  formData.append('user_id', userData.user_id);
                  formData.append('email', userData.email);
                  
                  const tokenResponse = await fetch(`${API_URL}/auth/get-token`, {
                    method: 'POST',
                    body: formData,
                    credentials: 'include'
                  });
                  
                  if (tokenResponse.ok) {
                    const tokenData = await tokenResponse.json();
                    if (tokenData.access_token && tokenData.access_token.split('.').length === 3) {
                      localStorage.setItem('auth_token', tokenData.access_token);
                      userData.auth_token = tokenData.access_token;
                      console.log('✅ Auth token retrieved via API and stored in localStorage');
                    } else {
                      throw new Error('Invalid token format from API');
                    }
                  } else {
                    throw new Error(`API returned ${tokenResponse.status}`);
                  }
                } catch (apiError) {
                  console.error('❌ Failed to fetch token via API:', apiError);
                  console.warn('⚠️ Auth token not available from URL, cookies, or API. API calls may fail if cookies are blocked.');
                  // Set a flag to indicate we need to fetch the token via API
                  localStorage.setItem('needs_token_refresh', 'true');
                  
                  // Show a warning to the user
                  toast.warning(
                    'Authentication token not received. If you experience issues, please enable cookies or try logging in again.',
                    {
                      toastId: 'token-warning',
                      autoClose: 5000,
                    }
                  );
                }
              }
            }
            
            // Set a flag to indicate this is a fresh Google auth
            localStorage.setItem('google_auth_success', 'true');
            localStorage.setItem('google_auth_timestamp', Date.now().toString());

            // Check for cookie issues but don't block the auth flow
            const cookieStatus = detectCookieIssues();
            if (cookieStatus.hasIssue) {
              console.warn("Cookie issues detected during Google auth callback:", cookieStatus);
              
              // Set a flag to check for cookie issues after redirect
              localStorage.setItem('check_cookie_after_auth', 'true');
              localStorage.setItem('check_cookie_timestamp', Date.now().toString());
              
              // Set the cookie issue flag but with a delay
              setTimeout(() => {
                setCookieIssueFlag();
              }, 2000);
              
              // Show a warning toast about cookie issues
              toast.warning(
                'Your browser may be blocking cookies which are required for authentication. Some features may not work properly.',
                {
                  toastId: 'cookie-warning-google',
                  autoClose: 5000,
                }
              );
            }
           
            // Success message
            toast.success("Successfully signed in with Google!");
            
            // Don't call fetchUserProfile here - let Header handle it
            // This prevents race conditions and authentication conflicts
            
            // Trigger a storage event to notify other components
            window.dispatchEvent(new Event('storage'));
            
            // Get the stored path or default to home
            const redirectPath = localStorage.getItem('preAuthPath') || '/';
            
            // Clear the stored path
            localStorage.removeItem('preAuthPath');
            
            
            // Short delay to allow browser to update localStorage before redirecting
            if (isComponentMounted) {
              setTimeout(() => {
                navigate(redirectPath, { replace: true });
              }, 100);
            }
          } catch (storageError) {
            console.error("Error storing auth data:", storageError);
            if (isComponentMounted) {
              toast.error("Error completing authentication. Please try again.");
              navigate('/', { replace: true });
            }
          }
        } else {
          console.error("Missing user data in callback");
          toast.error("Authentication failed: Missing user data");
          if (isComponentMounted) {
            navigate('/', { replace: true });
          }
        }
      } catch (error) {
        console.error("Auth callback error:", error);
        
        detectCookieIssues(); 
        setCookieIssueFlag();
        // toast.error("Authentication failed. Please try again.");
        if (isComponentMounted) {
          navigate('/', { replace: true });
        }
      } finally {
        if (isComponentMounted) {
          setIsLoading(false);
        }
      }
    };

    processOAuthCallback();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isComponentMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  return (
    <div className="flex items-center justify-center min-h-screen bg-black">
      <div className="text-center">
        <Lottie animationData={loadingAnimation} speed={0.5} loop={true} style={{ width: "50vh", height: "50vh" }} />
        <p className="mt-4 text-white text-xl font-spaceGrotesk">Completing authentication...</p>
      </div>
    </div>
  );
};

export default AuthCallback; 